import React, { useEffect, useRef, useState } from 'react';
import { FIRMWARE_CHANNELS, listPromotions, promoteFirmware } from '../../../api/firmware';
import { channelClass, formatDate } from '../firmwareUtils';
import FirmwareModal from './FirmwareModal';

const messageFor = (error) => error instanceof Error ? error.message : String(error);
const sortHistory = (items) => [...items].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));

export default function FirmwarePromotionDialog({ firmware, idToken, promotedBy, onClose, onPromoted, onPromotionsUpdated }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [toChannel, setToChannel] = useState('');
  const [confirming, setConfirming] = useState(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const bodyRef = useRef(null);
  const closeRef = useRef(onClose);
  const updateRef = useRef(onPromotionsUpdated);
  closeRef.current = onClose;
  updateRef.current = onPromotionsUpdated;
  const channels = FIRMWARE_CHANNELS.filter((channel) => history.some((item) => item.toChannel === channel));
  const targetChannels = FIRMWARE_CHANNELS.filter((item) => !channels.includes(item));

  useEffect(() => {
    mountedRef.current = true;
    const previousFocus = document.activeElement;
    const dialog = bodyRef.current.closest('[role="dialog"]');
    const controls = () => [...dialog.querySelectorAll('button, input, select, [tabindex]')]
      .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
    const handleKey = (event) => {
      if (event.key === 'Escape' && !savingRef.current) { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const focusable = controls();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    controls()[0]?.focus();
    dialog.addEventListener('keydown', handleKey);
    return () => {
      mountedRef.current = false;
      dialog.removeEventListener('keydown', handleKey);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setLoadError(''); setConfirming(false); setToChannel('');
    void listPromotions(idToken, { firmwareId: firmware.id })
      .then((promotions) => {
        if (!active) return;
        setHistory(sortHistory(promotions));
        updateRef.current?.(firmware.id, promotions);
      })
      .catch((requestError) => { if (active) setLoadError(messageFor(requestError)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [firmware.id, idToken, reload]);

  const confirmPromotion = async () => {
    if (savingRef.current || loading || loadError || !confirming || !targetChannels.includes(toChannel)) return;
    const target = toChannel;
    savingRef.current = true;
    setSaving(true); setError(''); setNotice('');
    try {
      const request = { firmwareId: firmware.id, toChannel: target };
      if (promotedBy) request.promotedBy = promotedBy;
      const promotion = await promoteFirmware(idToken, request);
      if (!mountedRef.current) return;
      setHistory((current) => sortHistory([promotion, ...current.filter((item) => item.id !== promotion.id)]));
      setConfirming(false); setToChannel('');
      setNotice(`Firmware promoted to ${promotion.toChannel}.`);
      onPromoted(promotion);
      // Promotion adds a channel association; it does not replace existing channels.
      try {
        const updated = await listPromotions(idToken, { firmwareId: firmware.id });
        if (!mountedRef.current) return;
        if (!updated.some((item) => item.firmwareId === firmware.id && item.toChannel === promotion.toChannel)) throw new Error('The channel association has not been confirmed yet.');
        setHistory(sortHistory(updated));
        updateRef.current?.(firmware.id, updated);
      } catch (requestError) {
        if (mountedRef.current) setLoadError(`Promotion was accepted, but promotion status refresh failed: ${messageFor(requestError)} Refresh status before promoting again.`);
      }
    } catch (requestError) { if (mountedRef.current) { setError(messageFor(requestError)); setConfirming(false); } }
    finally { savingRef.current = false; if (mountedRef.current) setSaving(false); }
  };

  const close = () => { if (!savingRef.current) onClose(); };
  return <FirmwareModal title={`Promote Firmware ${firmware.firmwareVersion}`} onClose={close}>
    <div ref={bodyRef} className="fw-promotion-body">
      <div className="fw-promotion-context"><div><strong>{firmware.deviceModel} {firmware.firmwareVersion}</strong><span>{firmware.hardwareVersion}</span><code>{firmware.id}</code></div><div><span>Promoted channels</span>{loading ? <span>Loading...</span> : channels.length ? channels.map((channel) => <span key={channel} className={channelClass(channel)}>{channel}</span>) : <span>Not promoted</span>}</div></div>
      {notice ? <div className="alert alert-success" role="status">{notice}</div> : null}
      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
      {loading ? <p role="status">Loading promotion history...</p> : loadError ? <div className="alert alert-error" role="alert"><p>{loadError}</p><button type="button" className="btn btn-secondary" onClick={() => { setError(''); setReload((value) => value + 1); }} disabled={saving}>Refresh status</button></div> : <>
        {targetChannels.length ? <form className="firmware-form" onSubmit={(event) => { event.preventDefault(); if (targetChannels.includes(toChannel)) { setError(''); setConfirming(true); } }}>
          <label><span>Target channel</span><select value={toChannel} onChange={(event) => { setToChannel(event.target.value); setConfirming(false); setError(''); }} disabled={saving}><option value="" disabled>Select channel</option>{targetChannels.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          {confirming ? <div className="fw-promotion-confirm" role="group" aria-label="Confirm promotion"><strong>Promote to {toChannel}</strong><p>Promote {firmware.deviceModel} {firmware.firmwareVersion} to {toChannel}?{toChannel === 'PRODUCTION' ? ' This adds the production channel to this package.' : ''}</p><div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={saving}>Cancel</button><button type="button" className="btn btn-primary" onClick={confirmPromotion} disabled={saving}>{saving ? 'Promoting...' : 'Confirm promotion'}</button></div></div> : <div className="firmware-modal-actions"><button type="submit" className="btn btn-primary" disabled={saving || !toChannel}>Promote firmware</button></div>}
        </form> : <p role="status">This firmware is promoted to all channels.</p>}
      </>}
      {!loading ? <section className="fw-promotion-history-section" aria-labelledby="promotion-history-heading"><h4 id="promotion-history-heading">Promotion history</h4>{history.length ? <ul className="fw-promotion-history">{history.map((item) => <li key={item.id}><span className={channelClass(item.toChannel)}>{item.toChannel}</span><span>{formatDate(item.createdAt)}</span><span>{item.promotedBy || 'Not recorded'}</span></li>)}</ul> : !loadError ? <p>No promotions yet.</p> : null}</section> : null}
      <div className="firmware-modal-actions"><button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>Done</button></div>
    </div>
  </FirmwareModal>;
}
